import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ClipboardCheck, ClipboardList, FileText, PackageCheck, Truck } from "lucide-react";

import OpenWorkQueueCards from "../../components/OpenWorkQueueCards";
import { StatusBadge } from "../../components/erp";
import {
  Button,
  EmptyState,
  ErrorState,
  Notice,
  PageHeader,
  SkeletonRows,
  Surface,
} from "../../../components/ui";
import { procurementAPI } from "../../services/businessErpService";

function WorkList({ id, labelledBy, title, description, rows, emptyText, href, numberKey, nameKey, statusKey = "status", extraRender }) {
  return (
    <Surface
      as="section"
      id={id}
      role="tabpanel"
      aria-labelledby={labelledBy}
      toolbar={(
        <div>
          <h2 className="text-section-title text-foreground">{title}</h2>
          <p className="mt-1 text-body-sm text-secondary">{description}</p>
        </div>
      )}
    >
      {!rows?.length ? (
        <EmptyState title="Nothing open" description={emptyText} />
      ) : (
        <ul className="divide-y divide-subtle">
          {rows.map((row) => (
            <li
              key={`${title}-${row.id}`}
              className="flex min-h-touch flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-label tabular-nums text-foreground">{row[numberKey] || row.id}</span>
                  <StatusBadge status={row[statusKey] || "OPEN"} />
                </div>
                <p className="mt-1 text-body-sm font-medium text-foreground">{row[nameKey] || "Untitled"}</p>
                {extraRender ? <p className="mt-1 text-meta text-tertiary">{extraRender(row)}</p> : null}
              </div>
              <Button
                as={Link}
                to={href}
                size="sm"
                variant="secondary"
                className="w-full border-accent/30 bg-accent-subtle/40 text-accent hover:border-accent hover:bg-accent-subtle sm:w-auto"
              >
                Open Module
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Surface>
  );
}

export default function ProcurementOpenWork() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await procurementAPI.openWork({ limit: 6 });
      setData(res?.data || null);
    } catch {
      setData(null);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary || {};
  const lanes = [
    {
      id: "requisitions",
      title: "Purchase Requisitions",
      description: "Submitted or approved requisitions still awaiting follow-through.",
      actionLabel: "Review requisitions",
      icon: ClipboardList,
      count: Number(summary.submittedPrCount || 0) + Number(summary.approvedPrCount || 0),
    },
    {
      id: "rfqs",
      title: "Request for Quotations",
      description: "RFQs still open or awaiting business action.",
      actionLabel: "Review RFQs",
      icon: FileText,
      count: Number(summary.openRfqCount || 0),
    },
    {
      id: "purchaseOrders",
      title: "Purchase Orders",
      description: "Orders still pending finance, supplier, or fulfillment action.",
      actionLabel: "Review orders",
      icon: ClipboardCheck,
      count: (data?.purchaseOrders || []).length,
    },
    {
      id: "deliveries",
      title: "Deliveries",
      description: "Supplier deliveries still in progress or open.",
      actionLabel: "Review deliveries",
      icon: Truck,
      count: Number(summary.openDeliveryCount || 0),
    },
    {
      id: "pendingReceipts",
      title: "Pending Receipts",
      description: "Delivered goods awaiting receipt confirmation.",
      actionLabel: "Review receipts",
      icon: PackageCheck,
      count: Number(summary.pendingReceiptCount || 0),
    },
  ];
  const requestedLane = searchParams.get("lane");
  const activeLane = lanes.some((item) => item.id === requestedLane)
    ? requestedLane
    : lanes.find((item) => item.count > 0)?.id ?? lanes[0].id;
  const panelId = "procurement-work-panel";
  const labelledBy = `procurement-queue-${activeLane}`;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Open Procurement Work"
        description="Track unfinished requisitions, RFQs, purchase orders, deliveries, and receipts that still need action."
      />

      <OpenWorkQueueCards
        queues={lanes}
        active={activeLane}
        loading={loading}
        onChange={(nextLane) => setSearchParams({ lane: nextLane })}
        idPrefix="procurement-queue"
        panelId={panelId}
        label="Procurement work queues"
      />

      {!loading && Number(summary.rfqAttentionCount || 0) > 0 ? (
        <Notice tone="warning">
          {summary.rfqAttentionCount} RFQ{Number(summary.rfqAttentionCount || 0) === 1 ? "" : "s"} missed their quote deadline and need business attention.
        </Notice>
      ) : null}

      {loading ? (
        <Surface id={panelId} role="tabpanel" aria-label="Loading procurement work">
          <SkeletonRows rows={5} columns={3} label="Loading procurement work" />
        </Surface>
      ) : null}
      {!loading && loadError ? (
        <Surface id={panelId} role="tabpanel" aria-label="Procurement work error">
          <ErrorState title="Could not load procurement work" description="Please try again." onRetry={load} />
        </Surface>
      ) : null}
      {!loading && !loadError && activeLane === "requisitions" ? (
        <WorkList
          id={panelId}
          labelledBy={labelledBy}
          title="Purchase Requisitions"
          description="Submitted requisitions still awaiting approval or approved requisitions not yet consumed into RFQ."
          rows={data?.requisitions || []}
          emptyText="There are no pending requisitions."
          href="/business/procurement/requisitions"
          numberKey="pr_number"
          nameKey="product_name"
          extraRender={(row) => `Requested qty: ${row.requested_quantity ?? 0}`}
        />
      ) : null}
      {!loading && !loadError && activeLane === "rfqs" ? (
        <WorkList
          id={panelId}
          labelledBy={labelledBy}
          title="Request for Quotations"
          description="RFQs still open, awaiting quotes, or waiting on business follow-through."
          rows={data?.rfqs || []}
          emptyText="There are no open RFQs."
          href="/business/procurement/rfqs"
          numberKey="rfq_number"
          nameKey="title"
          extraRender={(row) => row.deadline_at ? `Deadline: ${new Date(row.deadline_at).toLocaleDateString()}` : row.product_name}
        />
      ) : null}
      {!loading && !loadError && activeLane === "purchaseOrders" ? (
        <WorkList
          id={panelId}
          labelledBy={labelledBy}
          title="Purchase Orders"
          description="Purchase orders still pending finance, supplier action, or fulfillment."
          rows={data?.purchaseOrders || []}
          emptyText="There are no open purchase orders."
          href="/business/procurement/orders"
          numberKey="po_number"
          nameKey="supplier_name"
          extraRender={(row) => `${row.product_name || "Product"} · ${row.total_price_cents != null ? `₱${(Number(row.total_price_cents) / 100).toLocaleString()}` : "No total"}`}
        />
      ) : null}
      {!loading && !loadError && activeLane === "deliveries" ? (
        <WorkList
          id={panelId}
          labelledBy={labelledBy}
          title="Deliveries"
          description="Supplier-created deliveries that are still in progress or still open on the business side."
          rows={data?.deliveries || []}
          emptyText="There are no active deliveries."
          href="/business/logistics"
          numberKey="delivery_number"
          nameKey="supplier_name"
          statusKey="receiving_status"
          extraRender={(row) => `${row.po_number || "PO"} · ${row.product_name || "Product"} · Supplier status: ${row.status}`}
        />
      ) : null}
      {!loading && !loadError && activeLane === "pendingReceipts" ? (
        <WorkList
          id={panelId}
          labelledBy={labelledBy}
          title="Pending Receipts"
          description="Delivered shipments that still need business receipt confirmation or discrepancy closure."
          rows={data?.pendingReceipts || []}
          emptyText="There are no receipts waiting for confirmation."
          href="/business/inventory"
          numberKey="delivery_number"
          nameKey="supplier_name"
          statusKey="receiving_status"
          extraRender={(row) => `${row.po_number || "PO"} · ${row.product_name || "Product"}`}
        />
      ) : null}
    </div>
  );
}
